import React, { useEffect, useRef } from 'react';
import { Drink, Sale } from '../App';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import './StockDashboard.css';

interface StockDashboardProps {
  drinks: Drink[];
  salesHistory: Sale[];
}

const StockDashboard: React.FC<StockDashboardProps> = ({ drinks, salesHistory }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveringRef = useRef(false);
  const scrollDirectionRef = useRef(1);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollSpeed = 1; // pixels per frame
    
    const autoScroll = () => {
      if (!isHoveringRef.current && container) {
        const maxScroll = container.scrollHeight - container.clientHeight;
        
        if (container.scrollTop >= maxScroll - 5) {
          scrollDirectionRef.current = -1;
        } else if (container.scrollTop <= 5) {
          scrollDirectionRef.current = 1;
        }
        
        container.scrollTop += scrollSpeed * scrollDirectionRef.current;
      }
    };

    // Only set up the interval once
    if (!scrollIntervalRef.current) {
      scrollIntervalRef.current = setInterval(autoScroll, 50);
    }

    return () => {
      // Don't clear on every update, only on unmount
      if (drinks.length === 0) {
        if (scrollIntervalRef.current) {
          clearInterval(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }
      }
    };
  }, []); // Empty dependency array - only run once

  const handleMouseEnter = () => {
    isHoveringRef.current = true;
  };

  const handleMouseLeave = () => {
    isHoveringRef.current = false;
  };

  const getChartData = (drink: Drink) => {
    return drink.priceHistory.map((point, index) => ({
      time: index,
      price: point.price,
      timestamp: new Date(point.timestamp).toLocaleTimeString('de-DE', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    }));
  };

  const getTotalRevenue = () => {
    return salesHistory.reduce((sum, sale) => sum + sale.totalCost, 0);
  };

  const getTotalDrinksSold = () => {
    return salesHistory.reduce((sum, sale) => sum + sale.quantity, 0);
  };

  const getTopDrink = () => {
    const drinkCounts = drinks.map(d => ({
      ...d,
      totalSold: salesHistory
        .filter(s => s.drinkId === d.id)
        .reduce((sum, s) => sum + s.quantity, 0)
    }));
    return drinkCounts.sort((a, b) => b.totalSold - a.totalSold)[0];
  };

  const topDrink = getTopDrink();

  return (
    <div className="stock-dashboard">
      <div className="dashboard-header">
        <h2>📊 Trink-Börse Dashboard</h2>
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-label">Umsatz</div>
            <div className="stat-value">€{getTotalRevenue().toFixed(2)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Verkaufte Getränke</div>
            <div className="stat-value">{getTotalDrinksSold()}</div>
          </div>
          {topDrink && topDrink.totalSold > 0 && (
            <div className="stat-card">
              <div className="stat-label">Bestseller</div>
              <div className="stat-value">{topDrink.icon} {topDrink.name}</div>
            </div>
          )}
        </div>
      </div>

      <div 
        className="drinks-dashboard"
        ref={scrollContainerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {drinks.map(drink => {
          const priceChange = ((drink.currentPrice - drink.basePrice) / drink.basePrice * 100);
          const chartData = getChartData(drink);
          
          return (
            <div key={drink.id} className="drink-dashboard-card">
              <div className="drink-header">
                <div className="drink-info">
                  <span className="drink-icon">{drink.icon}</span>
                  <span className="drink-name">{drink.name}</span>
                </div>
                <div className="drink-stats">
                  <div className="current-price">
                    €{drink.currentPrice.toFixed(2)}
                  </div>
                  <div className={`price-change ${priceChange >= 0 ? 'positive' : 'negative'}`}>
                    {priceChange >= 0 ? '↑' : '↓'} {Math.abs(priceChange).toFixed(1)}%
                  </div>
                </div>
              </div>
              
              <div className="chart-container">
                {chartData.length > 1 ? (
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis 
                        dataKey="timestamp" 
                        stroke="#888"
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis 
                        stroke="#888"
                        tick={{ fontSize: 10 }}
                        domain={['dataMin - 0.5', 'dataMax + 0.5']}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                        formatter={(value: any) => `€${Number(value).toFixed(2)}`}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="price" 
                        stroke="#00ff88" 
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data">Noch keine Kursdaten</div>
                )}
              </div>
              
              <div className="drink-footer">
                <div className="footer-stat">
                  <span className="label">Basis:</span>
                  <span className="value">€{drink.basePrice.toFixed(2)}</span>
                </div>
                <div className="footer-stat">
                  <span className="label">Verkauft:</span>
                  <span className="value">{drink.purchases}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default StockDashboard;